import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Badge } from '@/components/ui/PageHeader';
import { providerReach, type OutcomeAnswer } from '@/api/provider-reach';

// ═══════════════════════════════════════════════════════════════════
// "Did they help?" inside one conversation.
//
// The member is asked three days after the conversation opened, once the
// business replied (job providers:ask-outcomes). The member sees their own
// answer and may answer while asked; the business sees only a confirmed yes.
// ═══════════════════════════════════════════════════════════════════

const ANSWERS: { value: OutcomeAnswer; label: string }[] = [
  { value: 'yes', label: 'Yes, they helped' },
  { value: 'not_yet', label: 'Not yet (ask me next week)' },
  { value: 'no', label: "It didn't work out" },
];

export function ThreadOutcomePanel({ threadId }: { threadId: string }) {
  const [message, setMessage] = useState<string | null>(null);
  const outcome = useQuery({
    queryKey: ['provider-thread-outcome', threadId],
    queryFn: () => providerReach.threadOutcome(threadId),
    retry: false,
  });
  const o = outcome.data?.data.outcome;

  async function answer(value: OutcomeAnswer) {
    setMessage(null);
    try {
      const res = await providerReach.answerOutcome(threadId, value, crypto.randomUUID());
      setMessage(res.data.offerVouch ? 'Kept. Offer the vouch sheet now.' : 'Kept.');
      await outcome.refetch();
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'Answer failed');
    }
  }

  if (!o) return null;
  return (
    <div className="text-xs border border-gray-100 rounded-lg px-3 py-2 space-y-2">
      <div className="flex items-center gap-2">
        <span className="font-medium text-gray-900">Did they help?</span>
        {o.confirmedWork && <Badge variant="green">work confirmed</Badge>}
        {o.answer && !o.confirmedWork && <Badge variant="gray">you said: {o.answer}</Badge>}
      </div>
      {o.canAnswer ? (
        <div className="flex gap-2 flex-wrap">
          {ANSWERS.map((a) => (
            <Button key={a.value} size="sm" variant="secondary" onClick={() => answer(a.value)}>{a.label}</Button>
          ))}
        </div>
      ) : (
        <p className="text-gray-500">Nothing to answer here now.</p>
      )}
      {message && <p className="text-gray-700">{message}</p>}
    </div>
  );
}
